import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/error';
import { type BopWsMessage, useBopWebSocket } from '@/lib/websocket';

export interface UseAsyncWsRequestOptions<TMessage extends BopWsMessage, TArg = void> {
  /** Starts the server-side job, receiving the value passed to `start`. Its response is only an acknowledgement; the push is the outcome. */
  request: (arg: TArg) => Promise<unknown>;
  /** The push that ends the wait */
  isMatch: (message: BopWsMessage) => message is TMessage;
  onMatch?: (message: TMessage) => void;
  timeoutMs: number;
  /** Shown as an info toast on timeout, since the job may still finish later */
  timeoutMessage: string;
  /** Prefix of the error toast when the request itself is rejected */
  requestErrorMessage: string;
}

export interface AsyncWsRequest<TArg = void> {
  isPending: boolean;
  start: (arg: TArg) => Promise<void>;
}

/**
 * Request-then-wait lifecycle for server-side jobs that report back over the shared websocket:
 * `isPending` holds from the click until a matching push or the timeout, whichever comes first.
 * The timer starts before the request so a hung await can never leave an un-timed spinner.
 */
export const useAsyncWsRequest = <TMessage extends BopWsMessage, TArg = void>({
  request,
  isMatch,
  onMatch,
  timeoutMs,
  timeoutMessage,
  requestErrorMessage,
}: UseAsyncWsRequestOptions<TMessage, TArg>): AsyncWsRequest<TArg> => {
  const { lastJsonMessage, lastMessage } = useBopWebSocket();
  const [isPending, setIsPending] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // `lastMessage` keeps the previous push around, so a second start must ignore it
  const messageBeforeStartRef = useRef<MessageEvent | null>(null);

  const stopWaiting = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsPending(false);
  }, []);

  useEffect(() => {
    if (!isPending || !lastJsonMessage || lastMessage === null || lastMessage === messageBeforeStartRef.current) return;
    if (!isMatch(lastJsonMessage)) return;
    stopWaiting();
    onMatch?.(lastJsonMessage);
  }, [isPending, lastJsonMessage, lastMessage, isMatch, onMatch, stopWaiting]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const start = async (arg: TArg) => {
    messageBeforeStartRef.current = lastMessage;
    setIsPending(true);
    timeoutRef.current = setTimeout(() => {
      setIsPending(false);
      toast.info(timeoutMessage);
    }, timeoutMs);
    try {
      await request(arg);
    } catch (error) {
      stopWaiting();
      toast.error(`${requestErrorMessage}: ${getErrorMessage(error)}`);
    }
  };

  return { isPending, start };
};
